import { createSignal, onMount, Show, useContext } from 'solid-js';
import { StyledErrorMessage, StyledFieldContainer, StyledShowpadCodeContainer } from './showpad-code-submission-style';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { addShowpadCode } from '../../tools/add-showpad-code';
import { Button } from '../../ui-components/button/button';
import { StyledTextInput } from '../../ui-components/inputs/text-input';
import { StyledFlexRow } from '../../ui-components/utility-style-components/flex';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Modal } from '../../ui-components/modal/modal';
import { StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { Text } from '../../ui-components/text/text';
import { TabHandler } from '../menu/menu-desktop/tab-handling';
import { ShowpadDelayInfoBox } from '../showpad-delay-info-box/showpad-delay-info-box';
import { ErrorCatcher } from '../../tools/error-catcher';
import { ArrowDownIcon } from '../icons-library/arrow-down';
import { ArrowUpIcon } from '../icons-library/arrow-up';
import theme from '../../style/theme';

export const ShowpadCodeSubmission = () => {
    const [errorMessage, setErrorMessage] = createSignal('');
    const [showModal, setShowModal] = createSignal(false);
    const [code, setCode] = createSignal('');
    const [submitting, setSubmitting] = createSignal(false);
    const [success, setSuccess] = createSignal(false);
    const [expanded, setExpanded] = createSignal(false);

    const { 
        localize,
        userState,
        updateUser,
        contactUsPage,
        viewport,
    } = useContext(AppContext);

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    onMount(() => {
        window.addEventListener('keydown', keydownListener);
    });

    const closeModal = () => {
        setShowModal(false);
    };

    const keydownListener = (e: any) => {
        if (showModal() && e.key === 'Escape') {
            closeModal();
        }
    };

    const handleInputChange = (value: string) => {
        setSuccess(false);
        setErrorMessage('');
        setCode(value);
    };

    const submitCode = async () => {
        setSubmitting(true);
        setSuccess(false);
        setErrorMessage('');

        const { success, message } = await addShowpadCode({ showpadCode: code() }, updateUser);
        setSubmitting(false);

        if (!success) {
            if (message === 'invalid-showpad-code') {
                setErrorMessage(localize('invalid-showpad-code', 'The E-Learning access code you entered is invalid'));
                setShowModal(true);
            } else {
                console.error('Error:', message);
                setErrorMessage(localize('error-has-occurred', 'An error has occurred, please try again later'));
            }

            return;
        }

        setCode('');
        setSuccess(true);
    };

    const HelpModal = () => (

        <TabHandler refs={{
            firstTabbableElement,
            lastTabbableElement,
        }}>
            <Modal
                visible={true}
                onClose={() => closeModal()}
                size='small'
                darkenBackground={true}
                closeButtonRef={(el: HTMLElement) => setFirstTabbableElement(el)}
            >
                <StyledVerticalSpace size={5} />
                <Text fontSize='large' fontStyle='bold'>
                    {localize('no-code-modal-header', 'Get access to E-Learning courses')}
                </Text>

                <Text fontSize='normal' splitParagraph={true}>
                    {localize('no-code-modal-text', 'Please contact your local administrator to get an E-Learning access code to access the E-Learning courses.')}
                </Text>

                <StyledFlexRow gap='2rem' justifyContent='center' alignItems='center'>
                    <Button
                        label={localize('close-modal-button-text', 'Close')}
                        variant='secondary'
                        onClick={closeModal}
                    />

                    <Button
                        label={localize('contact-us', 'Contact us')}
                        isLink={true}
                        url={contactUsPage.url}
                        element={(el: HTMLElement) => {
                            setLastTabbableElement(el);
                        }}
                    />
                </StyledFlexRow>

            </Modal>
        </TabHandler>
    );

    return (

        <ErrorCatcher componentName='ShowpadCodeSubmission' logErrorToServer={true}>
            <Show when={userState.user}>
                <StyledShowpadCodeContainer>
                    <div
                        class="flex flex-col desktop:flex-row items-left desktop:items-center justify-between gap-4 cursor-pointer py-4"
                        onClick={() => setExpanded(!expanded())}
                    >
                        <div class="flex flex-col desktop:flex-row">

                            <div class="text-normal">{localize('your-current-showpad-code', 'Your current access code')}:</div>
                            <div class="inline-block self-start desktop:ml-4 mt-2 desktop:mt-0 px-3 py-1 rounded-md text-small font-semibold border border-gray-light text-gray-dark">
                                {userState?.user?.showpadCode || localize('no-code-set', 'No code set')}
                            </div>
                        </div>

                        <Show when={!expanded()}>
                            <div class="flex flex-row items-center mt-3 desktop:mt-0">
                                {
                                    !userState?.user?.showpadCode ? <div class="text-normal mr-xxs font-bold">{localize('add-code', 'Add code')}</div> 
                                        : <div class="text-normal mr-xxs font-bold">{localize('change-code', 'Change code')}</div>
                                }
                                <ArrowDownIcon width={1.5} height={1.5} fill='#304b55'/>
                            </div>
                        </Show>

                        {/* Quick fix for the collapse button on smaller devices; dont show it at all. */}
                        <Show when={expanded() && viewport.width > theme.breakpoints.DESKTOP}>
                            <div class="flex flex-row items-center">
                                <ArrowUpIcon width={1.5} height={1.5} fill='#304b55'/>
                            </div>
                        </Show>
                    </div>

                    <div class="grid transition-all duration-200 ease-out"
                        style={{
                            'grid-template-rows': expanded() ? '1fr' : '0fr',
                            'opacity': expanded() ? 1 : 0,
                        }}
                    >
                        <div class="overflow-hidden">
                            <StyledFieldContainer>
                                <StyledVerticalSpace size={1} />

                                <StyledTextInput
                                    value={code()}
                                    onInput={(e) => handleInputChange(e.currentTarget.value)}
                                    placeholder={localize('enter-code-placeholder', 'Enter new access code')}
                                    hasErrors={false}
                                    name='showpad-code'
                                    monospace
                                    width='35rem'
                                />

                                <Button
                                    label={localize('submit-code', 'Submit Access code')}
                                    onClick={() => submitCode()}
                                    variant='secondary'
                                    noBlockSpacing
                                    disabled={submitting() || !code()}
                                />

                                <Show when={submitting()}>
                                    <LoadingSpinner size='2.2rem' />
                                </Show>

                                <Show when={errorMessage()}>
                                    <StyledErrorMessage>{errorMessage()}</StyledErrorMessage>
                                </Show>

                                <Show when={success()}>
                                    <StyledVerticalSpace size={0.5} />
                                    <ShowpadDelayInfoBox
                                        withoutPadding={false}
                                        text={localize(
                                            'showpad-code-successfully-added',
                                            'Your E-Learning access code has been successfully updated. Please note that it may take up to an hour for your access to be updated. Check back later to view your E-Learning courses.'
                                        )}
                                    />
                                </Show>
                            </StyledFieldContainer>

                            <Show when={showModal()}>
                                <HelpModal />
                            </Show>
                        </div>
                    </div>
                </StyledShowpadCodeContainer>
            </Show>
        </ErrorCatcher>
    );
};
